import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Kanban, TrendingUp, Megaphone, PieChart, Code2 } from 'lucide-react';

const solutions = [
  {
    id: 'hr',
    icon: Users,
    title: 'HR Management',
    subtitle: 'Complete human resource management from recruitment to retirement',
    description: 'Automate payroll, track attendance, manage leaves, and conduct performance reviews all in one platform.',
    features: ['Recruitment & Onboarding', 'Payroll Processing', 'Attendance Tracking', 'Performance Reviews'],
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&q=80',
    path: '/solutions/hr',
  },
  {
    id: 'project',
    icon: Kanban,
    title: 'Project Management',
    subtitle: 'Plan, execute, and deliver projects on time',
    description: 'Keep everyone aligned and productive with Kanban boards, Gantt charts, time tracking, and team collaboration tools.',
    features: ['Kanban Boards', 'Gantt Charts', 'Time Tracking', 'Team Collaboration'],
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80',
    path: '/solutions/project',
  },
  {
    id: 'sales',
    icon: TrendingUp,
    title: 'Sales Management',
    subtitle: 'Close more deals with powerful CRM',
    description: 'Track every opportunity from lead to close with pipeline management, automated quotations, invoicing, and detailed analytics.',
    features: ['Lead Management', 'Pipeline Tracking', 'Quotations & Invoices', 'Sales Analytics'],
    image: 'https://images.pexels.com/photos/3783585/pexels-photo-3783585.jpeg?w=600',
    path: '/solutions/sales',
  },
  {
    id: 'marketing',
    icon: Megaphone,
    title: 'Marketing Management',
    subtitle: 'Run multi-channel campaigns that deliver results',
    description: 'Automate email marketing, manage social media, score leads, and track ROI. Turn marketing spend into measurable results.',
    features: ['Email Campaigns', 'Social Media Management', 'Lead Scoring', 'ROI Analytics'],
    image: 'https://images.pexels.com/photos/5833267/pexels-photo-5833267.jpeg?w=600',
    path: '/solutions/marketing',
  },
  {
    id: 'finance',
    icon: PieChart,
    title: 'Finance Management',
    subtitle: 'Complete financial control in one dashboard',
    description: 'Make informed decisions with real-time insights from accounting, budgeting, expense tracking, invoicing, and compliance reporting.',
    features: ['Accounting', 'Budgeting', 'Expense Tracking', 'Financial Reports'],
    image: 'https://images.unsplash.com/photo-1618044733300-9472054094ee?w=600&q=80',
    path: '/solutions/finance',
  },
  {
    id: 'custom-it',
    icon: Code2,
    title: 'Custom IT Solutions',
    subtitle: 'Bespoke software for your unique needs',
    description: 'Our expert team builds custom web apps, mobile apps, APIs, and integrations tailored to your specific business requirements.',
    features: ['Web Applications', 'Mobile Apps', 'API Development', 'System Integration'],
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&q=80',
    path: '/solutions/custom-it',
  },
];

export const SolutionsPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="solutions-page">
      {/* Hero Banner */}
      <section className="py-20 md:py-28 hero-glow" data-testid="solutions-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 animate-fade-in-up">
            Our Solutions
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-white animate-fade-in-up stagger-1">
            Comprehensive Business Solutions
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mt-6 max-w-3xl mx-auto animate-fade-in-up stagger-2">
            Comprehensive business management tools designed to streamline every aspect of your operations.
          </p>
        </div>
      </section>

      {/* Solutions Z-Pattern */}
      <section className="py-16 md:py-24" data-testid="solutions-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24 md:space-y-32">
            {solutions.map((solution, index) => (
              <div
                key={solution.id}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`solution-block-${solution.id}`}
              >
                {/* Content */}
                <div className={`space-y-6 animate-fade-in-up ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="feature-icon">
                    <solution.icon className="w-6 h-6" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold font-heading text-white">
                    {solution.title}
                  </h2>
                  <p className="text-xl text-primary font-medium">
                    {solution.subtitle}
                  </p>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    {solution.description}
                  </p>
                  <ul className="grid grid-cols-2 gap-3">
                    {solution.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-muted-foreground">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={solution.path}>
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 font-medium glow-primary glow-primary-hover btn-shine mt-4"
                      data-testid={`btn-learn-more-${solution.id}`}
                    >
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                {/* Image */}
                <div className={`animate-fade-in-up stagger-2 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-[300px] md:h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;
